import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { Clock, User, Tag, GripVertical, Filter, Search, Plus, X, Send, CheckCircle, Building2, ChevronDown } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import { SOCKET_URL, SOCKET_PATH } from '../../config';

const statusConfig = [
  { name: 'Yeni', color: 'border-blue-400', bg: 'bg-blue-50', badge: 'bg-blue-500' },
  { name: 'İşleme Alındı', color: 'border-purple-400', bg: 'bg-purple-50', badge: 'bg-purple-500' },
  { name: 'Devam Ediyor', color: 'border-amber-400', bg: 'bg-amber-50', badge: 'bg-amber-500' },
  { name: 'Çözüldü', color: 'border-emerald-400', bg: 'bg-emerald-50', badge: 'bg-emerald-500' },
  { name: 'Logo\'ya İletildi', color: 'border-indigo-400', bg: 'bg-indigo-50', badge: 'bg-indigo-500' },
];

const priorityColors = {
  'Düşük': 'bg-slate-100 text-slate-600',
  'Normal': 'bg-blue-100 text-blue-700',
  'Yüksek': 'bg-orange-100 text-orange-700',
  'Acil': 'bg-red-100 text-red-700',
};

function TicketCard({ ticket, isDragging }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({ id: `ticket-${ticket.Id}`, data: { ticket } });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 1000 } : undefined;

  return (
    <div ref={setNodeRef} style={style} className={`bg-white rounded-lg p-2.5 shadow-sm border border-slate-200 hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-xs font-mono text-slate-400">#{ticket.Id}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${priorityColors[ticket.Priority] || ''}`}>{ticket.Priority}</span>
          </div>
          <Link to={`/staff/tickets/${ticket.Id}`} className="text-sm font-medium text-slate-800 hover:text-blue-600 line-clamp-2 block" onClick={e => e.stopPropagation()}>
            {ticket.Title}
          </Link>
        </div>
        <div {...attributes} {...listeners} className="p-1 text-slate-300 hover:text-slate-500 flex-shrink-0">
          <GripVertical size={16} />
        </div>
      </div>
      {ticket.Tags && ticket.Tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {ticket.Tags.map(tag => (
            <span key={tag.Id} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: tag.Color + '20', color: tag.Color }}>{tag.Name}</span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className="text-xs text-slate-400 flex items-center gap-1"><Tag size={10} />{ticket.Category?.split('/')[0]?.trim()}</span>
        <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={10} />{new Date(ticket.CreatedAt).toLocaleDateString('tr-TR')}</span>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-slate-500 flex items-center gap-1"><User size={10} />{ticket.ContactName || ticket.CreatedByName}{ticket.CustomerName ? ` • ${ticket.CustomerName}` : ''}</span>
        {ticket.AssignedToName && (
          <div className="flex items-center gap-1">
            <div className="w-5 h-5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
              {ticket.AssignedToName.charAt(0)}
            </div>
            <span className="text-xs text-slate-500">{ticket.AssignedToName}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusColumn({ status, tickets, isOver }) {
  const { setNodeRef } = useDroppable({ id: `status-${status.name}` });
  const columnTickets = tickets.filter(t => t.Status === status.name);

  return (
    <div ref={setNodeRef} className={`flex-1 min-w-[160px] rounded-xl ${isOver ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}>
      <div className={`border-t-3 ${status.color} rounded-xl bg-slate-50/80 min-h-[calc(100vh-220px)]`}>
        <div className="px-2 py-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${status.badge}`} />
            <h3 className="text-xs font-semibold text-slate-700 truncate">{status.name}</h3>
          </div>
          <span className="text-[10px] font-bold text-slate-400 bg-white px-1.5 py-0.5 rounded-full">{columnTickets.length}</span>
        </div>
        <div className="px-1.5 pb-2 space-y-1.5">
          {columnTickets.map(ticket => (
            <TicketCard key={ticket.Id} ticket={ticket} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function StaffTicketBoard() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', description: '', category: 'Genel', priority: 'Normal', type: 'Destek', contactEmail: '', contactName: '', customerId: '' });
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [identifiedUser, setIdentifiedUser] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) setShowCustomerDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    loadTickets();
    const socket = io(SOCKET_URL, { path: SOCKET_PATH });
    socket.on('newTicket', () => loadTickets());
    socket.on('ticketStatusChanged', () => loadTickets());
    socket.on('ticketClaimed', () => loadTickets());
    socket.on('ticketAssigned', () => loadTickets());
    return () => socket.disconnect();
  }, []);

  const loadTickets = async () => {
    try {
      const res = await api.get('/tickets');
      setTickets(res.data);
    } catch (err) {}
    setLoading(false);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      setOverColumn(over.id);
    } else {
      setOverColumn(null);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColumn(null);

    if (!over) return;

    const ticketId = active.data.current?.ticket?.Id;
    const newStatus = over.id.replace('status-', '');
    const currentTicket = tickets.find(t => t.Id === ticketId);

    if (!currentTicket || currentTicket.Status === newStatus) return;

    // Optimistic update
    setTickets(prev => prev.map(t => t.Id === ticketId ? { ...t, Status: newStatus } : t));

    try {
      await api.put(`/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Ticket #${ticketId} → ${newStatus}`);
    } catch (err) {
      toast.error('Durum güncellenemedi');
      loadTickets();
    }
  };

  const loadCustomers = async () => {
    try { const res = await api.get('/customers'); setCustomers(res.data); } catch (err) {}
  };

  const handleIdentifyEmail = async () => {
    if (!createForm.contactEmail) return;
    try {
      const res = await api.post('/public/identify', { email: createForm.contactEmail });
      if (res.data.found) {
        setIdentifiedUser(res.data);
        setCreateForm(prev => ({ 
          ...prev, 
          contactName: res.data.FullName || '',
          customerId: res.data.CustomerId ? String(res.data.CustomerId) : prev.customerId
        }));
        setCustomerSearch(res.data.CustomerName || '');
      } else {
        setIdentifiedUser(null);
      }
    } catch (err) {}
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setCreateForm({ ...createForm, contactEmail: val });
    if (identifiedUser) {
      setIdentifiedUser(null);
      setCreateForm(prev => ({ ...prev, contactEmail: val, contactName: '', customerId: '' }));
      setCustomerSearch('');
    }
  };

  const filteredCustomers = customers.filter(c =>
    !customerSearch || c.Name.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!createForm.title || !createForm.contactEmail || !createForm.contactName) { toast.error('E-posta, Ad Soyad ve Konu zorunludur!'); return; }
    if (!createForm.customerId && !(identifiedUser && identifiedUser.CustomerId)) { toast.error('Müşteri firma seçimi zorunludur!'); return; }
    setCreating(true);
    try {
      const res = await api.post('/tickets', createForm);
      toast.success(`Ticket #${res.data.ticketId} oluşturuldu ve müşteriye mail gönderildi!`);
      setShowCreateModal(false);
      setCreateForm({ title: '', description: '', category: 'Genel', priority: 'Normal', type: 'Destek', contactEmail: '', contactName: '', customerId: '' });
      setIdentifiedUser(null);
      setCustomerSearch('');
      loadTickets();
    } catch (err) { toast.error('Ticket oluşturulamadı'); }
    setCreating(false);
  };

  const filteredTickets = tickets.filter(t => {
    if (search && !t.Title.toLowerCase().includes(search.toLowerCase()) && !String(t.Id).includes(search)) return false;
    if (categoryFilter && t.Category !== categoryFilter) return false;
    return true;
  });

  const activeTicket = activeId ? tickets.find(t => `ticket-${t.Id}` === activeId) : null;

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-800">Ticket Board</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => { setShowCreateModal(true); loadCustomers(); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all">
            <Plus size={16} /> Yeni Ticket
          </button>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              placeholder="Ara..." />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Tüm Kategoriler</option>
            <option value="BPM / Flow Ürünleri">BPM / Flow</option>
            <option value="Logo Tiger / Go Ürünleri">Logo Tiger / Go</option>
            <option value="Bordro Ürünleri">Bordro</option>
            <option value="E-Devlet Ürünleri">E-Devlet</option>
            <option value="Özel Yazılım ve Servis">Özel Yazılım</option>
          </select>
          <button onClick={() => setShowResolved(!showResolved)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
              showResolved ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}>
            <CheckCircle size={16} /> {showResolved ? 'Çözülmüşler Gösteriliyor' : 'Çözülmüşleri Göster'}
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <div className="flex gap-2 pb-4">
          {statusConfig.filter(s => showResolved || s.name !== 'Çözüldü').map(status => (
            <StatusColumn key={status.name} status={status} tickets={filteredTickets} isOver={overColumn === `status-${status.name}`} />
          ))}
        </div>
        <DragOverlay>
          {activeTicket ? (
            <div className="w-56 bg-white rounded-xl p-3 shadow-2xl border-2 border-blue-400 rotate-2">
              <div className="flex items-center gap-1.5 mb-1">
                <span className="text-xs font-mono text-slate-400">#{activeTicket.Id}</span>
              </div>
              <p className="text-sm font-medium text-slate-800">{activeTicket.Title}</p>
              <p className="text-xs text-slate-400 mt-1">{activeTicket.CreatedByName}</p>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-slate-800">Müşteri Adına Ticket Oluştur</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
              {/* Müşteri E-posta */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Müşteri E-posta *</label>
                <input type="email" value={createForm.contactEmail} onChange={handleEmailChange} onBlur={handleIdentifyEmail} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="musteri@firma.com" />
                {identifiedUser && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">✓ Tanındı: {identifiedUser.FullName}{identifiedUser.CustomerName ? ` (${identifiedUser.CustomerName})` : ''}</p>}
              </div>

              {/* Müşteri Ad Soyad */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Müşteri Ad Soyad *</label>
                <input type="text" value={createForm.contactName} onChange={e => { if (!identifiedUser) setCreateForm({ ...createForm, contactName: e.target.value }); }} readOnly={!!identifiedUser} required
                  className={`w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 ${identifiedUser ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`} />
              </div>

              {/* Müşteri Firma */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Müşteri Firma *</label>
                {identifiedUser && identifiedUser.CustomerId ? (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-600 cursor-not-allowed">
                    <Building2 size={16} className="text-blue-500" />
                    <span className="text-sm font-medium">{identifiedUser.CustomerName}</span>
                  </div>
                ) : (
                  <div className="relative" ref={customerDropdownRef}>
                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                      <Search size={16} className="ml-3 text-slate-400 flex-shrink-0" />
                      <input type="text" value={customerSearch} onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); if (!e.target.value) setCreateForm({...createForm, customerId: ''}); }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        className="w-full px-3 py-2.5 focus:outline-none text-sm" placeholder="Firma ara..." />
                      {createForm.customerId && (
                        <button type="button" onClick={() => { setCreateForm({...createForm, customerId: ''}); setCustomerSearch(''); }}
                          className="mr-2 p-1 text-slate-400 hover:text-red-500"><X size={14} /></button>
                      )}
                      <ChevronDown size={16} className="mr-3 text-slate-400 flex-shrink-0" />
                    </div>
                    {showCustomerDropdown && (
                      <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                        {filteredCustomers.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-slate-400">Firma bulunamadı</p>
                        ) : filteredCustomers.map(c => (
                          <button key={c.Id} type="button" onClick={() => { setCreateForm({...createForm, customerId: String(c.Id)}); setCustomerSearch(c.Name); setShowCustomerDropdown(false); }}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 ${
                              String(c.Id) === createForm.customerId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'
                            }`}>
                            <Building2 size={14} className="text-slate-400 flex-shrink-0" />
                            {c.Name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Konu */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Konu *</label>
                <input type="text" value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })} required
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Açıklama</label>
                <textarea value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} rows={3}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Kategori</label>
                  <select value={createForm.category} onChange={e => setCreateForm({ ...createForm, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Genel">Genel</option>
                    <option value="BPM / Flow Ürünleri">BPM / Flow</option>
                    <option value="Logo Tiger / Go Ürünleri">Logo Tiger / Go</option>
                    <option value="Bordro Ürünleri">Bordro</option>
                    <option value="E-Devlet Ürünleri">E-Devlet</option>
                    <option value="Özel Yazılım ve Servis">Özel Yazılım</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Öncelik</label>
                  <select value={createForm.priority} onChange={e => setCreateForm({ ...createForm, priority: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Düşük">Düşük</option>
                    <option value="Normal">Normal</option>
                    <option value="Yüksek">Yüksek</option>
                    <option value="Acil">Acil</option>
                  </select>
                </div>
              </div>

              <button type="submit" disabled={creating}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 flex items-center justify-center gap-2">
                {creating ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : <><Send size={18} /> Oluştur ve Müşteriye Mail Gönder</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
